import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  Context,
  useCallback
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { getCurrentOfficial, OfficialData } from '../networkRequest/officialsService';
import { useNavigate } from 'react-router-dom';
import { checkTokenExpired } from '../utils/authorization';
import { createAccessTokenInterceptor } from '../networkRequest/api';

export type UserState = {
  accessToken: string;
  profile: OfficialData;
  email: string;
};

export type UserContextType = {
  user: UserState | null;
  loading: boolean;
  error: string | null;
};

export const UserContext: Context<UserContextType> = createContext<UserContextType>({
  user: null,
  loading: true,
  error: null,
});
// technically not a hook since it returns jsx, but the last line is a tiny custom hook and is nice for reusability.
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently, isLoading, isAuthenticated, logout } = useAuth0();
  const [state, setState] = useState<UserState | null>(null);
  const [hasFetchedProfile, setHasFetchedProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // give the interceptor the ability to refetch the profile / token so that it's universally reset for the app
  // in case of expired token when making a network request.
  useEffect(() => {
    createAccessTokenInterceptor(getAccessTokenSilently, setHasFetchedProfile, logout);
  }, [getAccessTokenSilently, logout]);

  const checkNeedNewToken = useCallback(() => {
    if (!state?.accessToken) return;

    const expirationResult = checkTokenExpired(state?.accessToken);

    if (!expirationResult.expired) return;

    if (expirationResult.expired) setHasFetchedProfile(false); // trigger the useEffect below to authenticate;

    if (expirationResult.error) {
      // if we implement global state for errors or similar, update this to show the error.
      console.error(expirationResult.error);
      navigate('/not-found');
    }
  }, [state, navigate]);

  // An alternative would be to choose an interval of time to call this function instead.
  // super quick change from a code standpoint and it would just check on an interval - to make sure a request doesn't fail, we still kee the 401 check/recall code.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkNeedNewToken();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkNeedNewToken]);

  useEffect(() => {
    // auth0 hasn't determined the auth state yet - keep loading until it has, otherwise
    // we prematurely mark ourselves "done" and let the app render before a profile exists.
    if (isLoading) return;

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    if (hasFetchedProfile) return;

    const fetchProfile = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        if (accessToken) {
          const { data: userProfile, error } = await getCurrentOfficial(accessToken);
          if (error) {
            throw new Error(error);
          }

          if (userProfile && userProfile.email) {
            const userObj = { email: userProfile.email, profile: userProfile, accessToken };
            setState(userObj);
            setHasFetchedProfile(true);
          } else {
            throw new Error('Unable to get current official.');
          }
        }
      } catch (error: any) {
        Sentry.captureException(error);
        setError(error.message);
        // we implemented this solution as an inbetween solution so that we can potentially refactor the context api usage in this app towards global state management of some kind or query management of some kind.
        navigate('/not-found');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [getAccessTokenSilently, isLoading, hasFetchedProfile, isAuthenticated, navigate]);

  return (
    <UserContext.Provider value={{ user: state, loading, error }}>{children}</UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
